import React, { forwardRef, type CSSProperties, type ReactNode } from "react";
import { mapBorderRadiusToken, mapColorToken, mapSpacingToken } from "./tokens";

type Dim = string | number | undefined;

export interface ViewProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  UNSAFE_style?: CSSProperties;
  UNSAFE_className?: string;
  width?: Dim;
  height?: Dim;
  minWidth?: Dim;
  maxWidth?: Dim;
  minHeight?: Dim;
  maxHeight?: Dim;
  margin?: string;
  marginX?: string;
  marginY?: string;
  marginTop?: string;
  marginBottom?: string;
  marginStart?: string;
  marginEnd?: string;
  padding?: string;
  paddingX?: string;
  paddingY?: string;
  paddingTop?: string;
  paddingBottom?: string;
  paddingStart?: string;
  paddingEnd?: string;
  position?: CSSProperties["position"];
  top?: Dim;
  left?: Dim;
  right?: Dim;
  bottom?: Dim;
  zIndex?: number | string;
  overflow?: CSSProperties["overflow"];
  flex?: string | number;
  flexGrow?: number;
  flexShrink?: number;
  flexBasis?: string | number;
  alignSelf?: string;
  gridArea?: string;
  backgroundColor?: string;
  borderRadius?: string;
  borderWidth?: string;
  borderColor?: string;
  borderStyle?: CSSProperties["borderStyle"];
  isHidden?: boolean;
}

/**
 * Layout container mirroring common `@adobe/react-spectrum` View props (v3-style API).
 * Spectrum v2 does not ship View; this maps semantic props onto a div + inline styles.
 */
const View = forwardRef<HTMLDivElement, ViewProps>((props, ref) => {
  const {
    children,
    UNSAFE_style,
    UNSAFE_className,
    width,
    height,
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,
    margin,
    marginX,
    marginY,
    marginTop,
    marginBottom,
    marginStart,
    marginEnd,
    padding,
    paddingX,
    paddingY,
    paddingTop,
    paddingBottom,
    paddingStart,
    paddingEnd,
    position,
    top,
    left,
    right,
    bottom,
    zIndex,
    overflow,
    flex,
    flexGrow,
    flexShrink,
    flexBasis,
    alignSelf,
    gridArea,
    backgroundColor,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    isHidden,
    className,
    style,
    ...rest
  } = props;

  if (isHidden) {
    return null;
  }

  const pad = padding ? (mapSpacingToken(padding) ?? padding) : undefined;
  const padX = paddingX ? (mapSpacingToken(paddingX) ?? paddingX) : undefined;
  const padY = paddingY ? (mapSpacingToken(paddingY) ?? paddingY) : undefined;
  const mar = margin ? (mapSpacingToken(margin) ?? margin) : undefined;
  const marX = marginX ? (mapSpacingToken(marginX) ?? marginX) : undefined;
  const marY = marginY ? (mapSpacingToken(marginY) ?? marginY) : undefined;

  const merged: CSSProperties = {
    width,
    height,
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,
    margin: mar,
    marginTop: marginTop
      ? (mapSpacingToken(marginTop) ?? marginTop)
      : undefined,
    marginBottom: marginBottom
      ? (mapSpacingToken(marginBottom) ?? marginBottom)
      : undefined,
    marginInlineStart: marginStart
      ? (mapSpacingToken(marginStart) ?? marginStart)
      : undefined,
    marginInlineEnd: marginEnd
      ? (mapSpacingToken(marginEnd) ?? marginEnd)
      : undefined,
    marginInline: marX,
    marginBlock: marY,
    padding: pad,
    paddingTop: paddingTop
      ? (mapSpacingToken(paddingTop) ?? paddingTop)
      : undefined,
    paddingBottom: paddingBottom
      ? (mapSpacingToken(paddingBottom) ?? paddingBottom)
      : undefined,
    paddingInlineStart: paddingStart
      ? (mapSpacingToken(paddingStart) ?? paddingStart)
      : undefined,
    paddingInlineEnd: paddingEnd
      ? (mapSpacingToken(paddingEnd) ?? paddingEnd)
      : undefined,
    paddingInline: padX,
    paddingBlock: padY,
    position,
    top,
    left,
    right,
    bottom,
    zIndex,
    overflow,
    flex,
    flexGrow,
    flexShrink,
    flexBasis,
    alignSelf: alignSelf as CSSProperties["alignSelf"],
    gridArea,
    backgroundColor: mapColorToken(backgroundColor) ?? backgroundColor,
    borderRadius: mapBorderRadiusToken(borderRadius) ?? borderRadius,
    borderWidth,
    borderColor: mapColorToken(borderColor) ?? borderColor,
    borderStyle,
    ...UNSAFE_style,
    ...style,
  };

  return (
    <div
      ref={ref}
      className={[UNSAFE_className, className].filter(Boolean).join(" ")}
      style={merged}
      {...rest}
    >
      {children}
    </div>
  );
});

export default View;
